import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { BUSINESS } from "@/data/business";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.15 * i, ease: [0.16, 1, 0.3, 1] },
  }),
};

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = ["MANASA", "CONSUMER", "ELECTRONIC", "SERVICES"];

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-24"
    >
      {/* Parallax texture */}
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1592659762303-90081d34b277?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="circuit board macro"
          className="w-full h-[120%] object-cover opacity-20 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="absolute inset-0 grid-lines opacity-[0.15]" />
      </motion.div>

      <motion.div style={{ opacity }} className="max-w-[1600px] mx-auto w-full px-5 md:px-10 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-mono-plex font-bold">
            Est. Hyderabad — Repair & Install
          </span>
          <span className="h-px flex-1 bg-[#222222] max-w-[200px]" />
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.82] tracking-tighter text-white text-[14vw] md:text-[12vw] lg:text-[10vw]">
          {lines.map((ln, i) => (
            <span key={ln} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {ln === "SERVICES" ? (
                  <span>SERVIC<span className="text-[#FF3B30]">ES</span></span>
                ) : (
                  ln
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="text-[#A0A0A0] text-sm md:text-base max-w-md font-mono-plex leading-relaxed">
            {BUSINESS.tagline} We service leading brands the moment their warranty runs out.
          </p>
          <button
            data-testid="hero-cta"
            onClick={() => scrollTo("#contact")}
            className="group flex items-center gap-4 self-start"
          >
            <span className="bg-white text-black px-8 py-4 uppercase tracking-[0.15em] text-xs font-bold group-hover:bg-[#FF3B30] group-hover:text-white transition-all font-mono-plex">
              Book a Repair
            </span>
            <span className="w-12 h-12 border border-[#222222] flex items-center justify-center group-hover:border-[#FF3B30] transition-colors">
              <ArrowDown className="w-4 h-4 text-white" />
            </span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
